{
  "title": "Billing",
  "description": "Dodo Payments takes the payments and issues the invoices.",
  "tabs": {
    "label": "Billing sections",
    "overview": "Overview",
    "methods": "Payment methods",
    "details": "Billing details",
    "invoices": "Invoices",
    "payments": "Payments"
  },
  "roles": {
    "readOnly": "Only the owner, admins and billing members can make payments or change billing."
  },
  "kinds": {
    "first_charge": "First charge",
    "renewal": "Renewal",
    "plan_change": "Plan change",
    "ai_credits": "AI credits"
  },
  "failure": {
    "card_declined": "Your bank declined the payment. Try another payment method, or ask your bank why.",
    "insufficient_funds": "The card doesn't have enough balance. Top it up or use another payment method.",
    "expired_card": "The card has expired. Add a new card on the payment page and try again.",
    "authentication_failed": "The bank verification wasn't completed. Try again and finish the verification step."
  },
  "failureShort": {
    "card_declined": "Declined by the bank",
    "insufficient_funds": "Insufficient balance",
    "expired_card": "Card expired",
    "authentication_failed": "Verification not completed"
  },
  "amounts": {
    "subtotal": "Subtotal",
    "vat": "VAT {{rate}}",
    "vatShort": "VAT {{rate}}",
    "vatZeroShort": "No VAT (outside Saudi Arabia)",
    "total": "Total",
    "dueNow": "Due now",
    "inclVat": "incl. VAT",
    "inclVatRate": "incl. VAT {{rate}}",
    "zeroRated": "No VAT: services for clients outside Saudi Arabia are zero rated."
  },
  "lines": {
    "period": "{{from}} to {{to}}",
    "subscription": "{{plan}} plan, {{cycle}}",
    "proration": "{{plan}} plan, {{cycle}}, rest of the period",
    "prorationCredit": "Credit for unused time on {{plan}}, {{cycle}}",
    "aiCredits_one": "{{credits}} AI credits (1 pack)",
    "aiCredits_other": "{{credits}} AI credits ({{count}} packs)",
    "extraAccounts_one": "1 extra hosting account",
    "extraAccounts_other": "{{count}} extra hosting accounts"
  },
  "stepper": {
    "less": "Less",
    "more": "More"
  },
  "mock": {
    "payments": "Demo mode: no real payment is made and nothing is charged."
  },
  "trial": {
    "badge_one": "1 day left in your trial",
    "badge_other": "{{count}} days left in your trial",
    "title": "Set up payment before your trial ends on {{date}}",
    "body": "Your server keeps going after the trial only with payment set up. Without it, the control panel locks as soon as the trial ends; websites and mail keep running.",
    "noChargeNow": "Nothing is charged today.",
    "steps": {
      "started": "Trial started",
      "firstCharge": "First charge on {{date}}",
      "setUp": "Set up payment before {{date}}",
      "setUpDone": "Payment set up"
    },
    "firstChargeLabel": "First charge",
    "cancelAnytime": "Cancel any time before the trial ends and nothing is charged.",
    "titleSet": "Your trial ends on {{date}}",
    "bodySet": "You're all set. We'll charge the payment method saved with Dodo Payments when the trial ends, and you'll get the invoice by email.",
    "setUp": "Set up payment",
    "ready": "Ready",
    "notReady": "Payment not set up"
  },
  "alerts": {
    "label": "Billing alerts",
    "paymentFailed": {
      "title": "The renewal payment for {{server}} failed:",
      "action": "Pay now",
      "retry": "We'll try again on {{date}}.",
      "grace": "The server runs normally until {{date}}; after that, the control panel locks until you pay. Websites and mail keep running."
    },
    "cancelScheduled": "{{server}} is cancelled and ends on {{date}}, when its control panel locks. Websites and mail keep running.",
    "changeScheduled": "{{server}} moves to the {{plan}} plan, {{cycle}}, on {{date}}.",
    "detailsIncomplete": "Add your billing details so your invoices carry your company name and VAT number",
    "completeDetails": "Add details",
    "locked": {
      "title": "The control panel on {{server}} is locked",
      "action": "Renew now"
    },
    "paymentNeeded": {
      "title": "{{server}} is waiting for its first payment",
      "body": "Pay the first period to continue installing the server.",
      "action": "Pay"
    }
  },
  "figures": {
    "nextCharge": "Next charge",
    "none": "None",
    "nextChargeOn": "On {{date}}",
    "nothingScheduled": "No renewal is scheduled.",
    "monthly": "Monthly cost",
    "monthlyHint": "All servers, per month, before VAT",
    "method": "Default payment method",
    "noMethod": "None yet",
    "noMethodHint": "Saved with your first payment",
    "manage": "Update",
    "details": "Details",
    "methodHint": "For renewals and automatic credit packs"
  },
  "subscriptions": {
    "title": "Subscriptions",
    "description": "Each server has its own subscription.",
    "empty": {
      "title": "No subscriptions yet",
      "body": "Each server you add has its own subscription.",
      "action": "Add a server"
    },
    "when": {
      "trial": "Trial until {{date}}",
      "pending": "Billing starts when the server is activated",
      "failed": "Payment failed on {{date}}.",
      "retry": "Next try on {{date}}.",
      "ends": "Ends on {{date}}",
      "renews": "Renews on {{date}}",
      "change": "Moves to {{plan}}, {{cycle}}, on {{date}}",
      "incomplete": "Waiting for the first payment before install",
      "trialNoPayment": "Trial ends on {{date}}, payment not set up",
      "locksOn": "The control panel locks on {{date}} if unpaid."
    },
    "actions": {
      "change": "Change plan",
      "pay": "Pay now",
      "dropChange": "Keep the current plan",
      "keep": "Keep subscription",
      "server": "Server plan page",
      "invoices": "Invoices for this server",
      "more": "More actions",
      "payFor": {
        "first_period": "Pay first period",
        "trial_conversion": "Set up payment",
        "past_due": "Pay now",
        "renewal": "Renew now"
      }
    },
    "perYear": "a year",
    "perMonth": "a month",
    "removedServer": "Removed server",
    "changeDropped": "The scheduled change was dropped. The current plan stays."
  },
  "recent": {
    "title": "Latest invoices",
    "all": "All invoices"
  },
  "methods": {
    "title": "Payment methods",
    "description": "The default method pays renewals and automatic credit packs.",
    "default": "Default",
    "endingIn": "{{brand}} ending in {{last4}}",
    "trialTitle": "Set up payment before your trial ends",
    "trialBody": "Save a payment method so your server keeps going after the trial. Nothing is charged today.",
    "empty": {
      "title": "No payment methods yet",
      "body": "A payment method is saved with your first payment, then it shows here."
    },
    "manage": "Update payment method",
    "where": {
      "title": "Where your payment methods are kept",
      "body": "Dodo Payments keeps your payment methods; we never store card details. We show only the card brand and last 4 digits, or the PayPal account. A new method replaces the current one on all your subscriptions."
    },
    "added": "Added {{date}}",
    "paypalAccount": "PayPal account {{account}}"
  },
  "change": {
    "title": "Change plan",
    "description": "Current plan: {{plan}}, {{cycle}}.",
    "confirm": "Confirm change",
    "continueToPay": "Continue to payment",
    "pickOther": "Pick a different plan or billing cycle to see the change.",
    "prorationNote": "You pay only for the rest of the current period, minus the unused time on your current plan.",
    "scheduled": "The change to {{plan}} is scheduled for {{date}}",
    "applied": "The server is now on {{plan}}",
    "timing": {
      "now": "Takes effect now",
      "periodEnd": "Takes effect on {{date}}, at the end of the current period. Nothing is charged now.",
      "free": "Takes effect now. Nothing to pay until billing starts."
    },
    "nextRenewal": "Next renewal on {{date}}:"
  },
  "invoices": {
    "caption": "Invoices",
    "search": "Search by invoice number",
    "cols": {
      "number": "Invoice",
      "date": "Date",
      "servers": "Servers",
      "amount": "Amount",
      "vat": "VAT",
      "total": "Total",
      "status": "Status"
    },
    "filters": {
      "status": "Status",
      "kind": "Type",
      "server": "Server"
    },
    "actions": {
      "view": "View invoice",
      "pay": "Pay now",
      "download": "Download invoice"
    },
    "empty": {
      "title": "No invoices yet",
      "body": "Invoices appear here after your first charge."
    }
  },
  "payments": {
    "caption": "Payments",
    "search": "Search by invoice number",
    "cols": {
      "date": "Date",
      "invoice": "Invoice",
      "method": "Method",
      "amount": "Amount",
      "status": "Status"
    },
    "kind": {
      "charge": "Payment",
      "retry": "Retry",
      "refund": "Refund"
    },
    "automatic": "automatic",
    "filters": {
      "status": "Status"
    },
    "empty": {
      "title": "No payments yet",
      "body": "Every payment attempt and retry shows here."
    }
  },
  "invoice": {
    "title": "Invoice",
    "description": "Invoice and payment details.",
    "payNow": "Pay",
    "attempts": "Payment attempts",
    "noAttempts": "No payment attempts yet.",
    "failedTitle": "The payment for this invoice failed",
    "openBody": "This invoice is waiting for payment.",
    "download": "Download invoice",
    "summary": "Invoice summary",
    "number": "Invoice number",
    "issued": "Issued",
    "paidOn": "Paid on",
    "due": "Due",
    "status": "Status",
    "billedTo": "Billed to",
    "vatNumber": "VAT number",
    "crNumber": "CR number",
    "paidWith": "Paid with {{method}} ending in {{last4}} on {{date}}.",
    "refundedOn": "Fully refunded on {{date}}.",
    "reference": "Reference",
    "amountDue": "Amount due",
    "issuedByDodo": "Dodo Payments issues the invoice and receipt and handles VAT. Download saves a PDF of the official document.",
    "documentPending": "Dodo Payments issues the official document once it's paid.",
    "voidBody": "We voided this invoice because the subscription ended unpaid. Renewing issues a new invoice.",
    "paidWithPaypal": "Paid with PayPal on {{date}}."
  },
  "details": {
    "company": "Company",
    "legalName": "Legal name",
    "legalNameHint": "As registered; it appears on your invoices.",
    "country": "Billing country",
    "countryHint": "Decides the VAT on your invoices.",
    "cr": "Commercial registration (CR) number",
    "crHintSa": "10 digits",
    "crHint": "As on your trade licence",
    "noVat": "{{country}} has no VAT yet, so invoices carry no VAT number.",
    "vat": "VAT registration number",
    "vatHint": {
      "SA": "15 digits, starts and ends with 3. With it, your invoices carry your company name and VAT number.",
      "AE": "Your TRN: 15 digits starting with 100",
      "BH": "15 digits",
      "OM": "OM followed by 10 digits"
    },
    "address": "Address",
    "addressHint": "Needed on business invoices when you have a VAT number.",
    "line1": "Street and building",
    "line2": "District, unit or additional number",
    "city": "City",
    "region": "Region",
    "postal": "Postal code",
    "emails": "Invoice emails",
    "emailsHint": "Invoices and receipts go to these addresses. Up to 5.",
    "primaryEmail": "Primary email",
    "ccEmail": "Email {{n}}",
    "removeEmail": "Remove email",
    "addEmail": "Add email",
    "saved": "Billing details saved",
    "dodo": {
      "title": "Your invoices come from Dodo Payments",
      "body": "Dodo Payments is the merchant of record and issues your invoices and receipts. We pass these details to Dodo so business invoices carry your name and VAT number.",
      "vatSa": "In Saudi Arabia, 15% VAT is added; with a VAT number, the invoice names your company as the buyer.",
      "vatGcc": "Outside Saudi Arabia, Dodo Payments works out the VAT for your country and shows it on the invoice.",
      "future": "Changes apply to new invoices. Past invoices keep their details."
    }
  },
  "pay": {
    "continue": "Continue to payment",
    "setUp": "Continue to save a payment method",
    "firstChargeOn": "First charge on {{date}}",
    "later": "Charged later:",
    "first_period": {
      "title": "Pay the first period for {{server}}",
      "body": "The server needs its first payment before the install. Once it's paid, you continue the install."
    },
    "trial_conversion": {
      "title": "Set up payment for {{server}}",
      "body": "You save a payment method now and nothing is charged today. The first charge is on {{date}}, when the trial ends."
    },
    "past_due": {
      "title": "Pay the renewal for {{server}}",
      "body": "The renewal payment didn't go through. The server runs normally until {{date}}; after that, the control panel locks until you pay."
    },
    "renewal": {
      "title": "Renew {{server}}",
      "body": "The subscription ended on {{date}} and the control panel is locked. Renewing starts a new period from the day you pay.",
      "choose": "How to renew",
      "keep": "Same plan: {{plan}}, {{cycle}}",
      "keepBody": "At your price",
      "switch": "Another plan or billing cycle",
      "switchBody": "At today's price for the plan you pick.",
      "unlock": "After you pay, the control panel unlocks at the server's next check-in with bPanel, usually within minutes. Websites and mail never stopped."
    }
  },
  "pricing": {
    "yourPrice": "Your price",
    "todayShort": "Today's price",
    "switchRule": "Any change of plan or billing cycle, upgrade or downgrade, moves the subscription to the new plan's price today. That price applies to the change charge and to every renewal after it.",
    "keepRule": "Renewing on the same plan and billing cycle keeps your price.",
    "newPrice": "{{plan}}, {{cycle}}, from now on:",
    "leavingYourPrice": "Your current price, which you leave behind:",
    "todayPrice": "Today's price for your current plan:"
  },
  "lock": {
    "reason": {
      "cancelled": "The subscription ended on {{date}} after it was cancelled.",
      "trial_ended": "The trial ended on {{date}} without payment set up.",
      "unpaid": "The subscription ended on {{date}} because the renewal wasn't paid within the grace period.",
      "revoked": "We revoked this server's licence on {{date}}. Contact support."
    },
    "keepsRunning": "Websites, mail, databases and DNS keep running, and nothing is deleted."
  },
  "checkout": {
    "title": "Payment",
    "back": "Back",
    "summary": "Order summary",
    "purpose": {
      "first_period": "First period for the server",
      "trial_conversion": "Save a payment method",
      "plan_change": "Plan change",
      "past_due": "Renewal payment",
      "renewal": "Subscription renewal",
      "credits": "AI credit packs",
      "payment_method": "Update payment method"
    },
    "dueToday": "Due today",
    "later": "First charge on {{date}}:",
    "nothingToday": "Nothing is charged today.",
    "methodBody": "The new method replaces the current one for upcoming renewals. Nothing is charged now, except an overdue renewal if there is one.",
    "frame": "Payment form",
    "opening": "Loading the payment form",
    "confirming": {
      "title": "Confirming your payment",
      "body": "This takes a few seconds.",
      "charged": "The difference is charged to your saved payment method. Confirming takes a few seconds.",
      "method": "Confirming your new payment method. This takes a few seconds."
    },
    "slow": {
      "title": "Confirmation is taking longer",
      "body": "The payment went through, but the confirmation hasn't reached us yet. You can continue; your billing updates when it arrives."
    },
    "error": {
      "title": "The payment form didn't open",
      "body": "Nothing was charged. Try again in a moment."
    },
    "expired": {
      "title": "This payment session expired",
      "body": "Nothing was charged. Go back and start the payment again."
    },
    "failed": {
      "title": "The payment didn't go through",
      "body": "It was declined and nothing was charged. Go back and try again, or use another payment method."
    },
    "notFound": "This payment session doesn't exist or has expired. Go back and start the payment again.",
    "retry": "Try again",
    "continue": "Continue",
    "paid": "Paid",
    "done": {
      "first_period": {
        "title": "Payment received",
        "body": "The first period for {{server}} is paid. You can continue the install now."
      },
      "trial_conversion": {
        "title": "Payment method saved",
        "body": "{{server}} is all set. The first charge is when the trial ends."
      },
      "past_due": {
        "title": "Payment received",
        "body": "The renewal for {{server}} is paid, and the subscription is active."
      },
      "renewal": {
        "title": "Subscription renewed",
        "body": "{{server}} is renewed. Its control panel unlocks at the server's next check-in with bPanel."
      },
      "plan_change": {
        "title": "Plan changed",
        "body": "{{server}} is now on the new plan, at today's price."
      },
      "credits": {
        "title": "Credits added",
        "body": "The credits are now in the balance of {{server}}."
      },
      "payment_method": {
        "title": "Payment method updated",
        "body": "Upcoming renewals use the new method."
      }
    }
  }
}
